// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/ROLE_PERMISSION_MANAGEMENT_PLAN.md
/**
 * Role Management Component
 * 
 * Main component for managing roles and their permissions.
 * Integrated into User Management section.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Lock as LockIcon
} from '@mui/icons-material';

import PermissionMatrix from './PermissionMatrix';
import RoleEditorDialog from './RoleEditorDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import AlertDialog from '../common/AlertDialog';
import roleManagementService from '../../services/roleManagementService';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissionMatrix, setPermissionMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, data: null });
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', severity: 'info' });

  // Load roles on mount
  useEffect(() => {
    loadRoles();
  }, []);

  // Load permission matrix when role selected
  useEffect(() => {
    if (selectedRoleId) {
      loadPermissionMatrix(selectedRoleId);
    }
  }, [selectedRoleId]);

  const loadRoles = async () => {
    try {
      const data = await roleManagementService.getRoles();
      setRoles(data);
      
      // Auto-select first role if none selected
      if (!selectedRoleId && data.length > 0) {
        setSelectedRoleId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load roles:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to load roles',
        severity: 'error'
      });
    }
  };

  const loadPermissionMatrix = async (roleId) => {
    setLoading(true);
    setPermissionChanges({});
    setHasChanges(false);
    
    try {
      const data = await roleManagementService.getPermissionMatrix(roleId);
      setPermissionMatrix(data);
      setSelectedRole(data.role);
    } catch (err) {
      console.error('Failed to load permission matrix:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to load permission matrix',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (event) => {
    if (hasChanges) {
      setConfirmDialog({
        open: true,
        action: 'changeRole',
        data: event.target.value
      });
    } else {
      setSelectedRoleId(event.target.value);
    }
  };

  const handlePermissionChange = useCallback((permissionId, granted) => {
    setPermissionChanges((prev) => ({ ...prev, [permissionId]: granted }));
    setHasChanges(true);
  }, []);

  const handleSave = async () => {
    if (!selectedRoleId || Object.keys(permissionChanges).length === 0) {
      return;
    }

    setSaving(true);
    
    try {
      // Convert changes to API format
      const updates = Object.entries(permissionChanges).map(([permissionId, granted]) => ({
        permission_id: permissionId,
        granted
      }));

      await roleManagementService.bulkUpdatePermissions(selectedRoleId, updates);
      
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Updated ${updates.length} permissions successfully`,
        severity: 'success'
      });
      
      // Reload matrix
      await loadPermissionMatrix(selectedRoleId);
      setPermissionChanges({});
      setHasChanges(false);
      
    } catch (err) {
      console.error('Failed to save permissions:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to save permissions',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setConfirmDialog({
      open: true,
      action: 'cancel',
      data: null
    });
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setRoleEditorOpen(true);
  };

  const handleSaveRole = async (roleData) => {
    try {
      const newRole = await roleManagementService.createRole(roleData);
      
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Role "${roleData.name}" created successfully`,
        severity: 'success'
      });
      
      // Reload roles and select new role
      await loadRoles();
      setSelectedRoleId(newRole.id);
      
    } catch (err) {
      throw err; // Re-throw to be handled by dialog
    }
  };

  const handleDeleteRole = () => {
    if (!selectedRole || selectedRole.code === 'ADMIN') {
      setAlertDialog({
        open: true,
        title: 'Cannot Delete',
        message: 'Administrator role cannot be deleted',
        severity: 'warning'
      });
      return;
    }

    setConfirmDialog({
      open: true,
      action: 'deleteRole',
      data: selectedRole
    });
  };

  const confirmDeleteRole = async () => {
    try {
      await roleManagementService.deleteRole(selectedRole.id);
      
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Role "${selectedRole.name}" deleted successfully`,
        severity: 'success'
      });
      
      // Reload roles and select first role
      await loadRoles();
      setSelectedRoleId('');
      setSelectedRole(null);
      setPermissionMatrix(null);
      
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to delete role',
        severity: 'error'
      });
    }
  };

  const confirmAction = async () => {
    const { action, data } = confirmDialog;
    
    if (action === 'cancel') {
      // Discard changes
      setPermissionChanges({});
      setHasChanges(false);
      await loadPermissionMatrix(selectedRoleId);
    } else if (action === 'changeRole') {
      // Change role without saving
      setPermissionChanges({});
      setHasChanges(false);
      setSelectedRoleId(data);
    } else if (action === 'deleteRole') {
      // Delete role
      await confirmDeleteRole();
    }
    
    setConfirmDialog({ open: false, action: null, data: null });
  };

  return (
    <Box>
      {/* Header and Role Selection */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={selectedRoleId}
                  label="Select Role"
                  onChange={handleRoleChange}
                  disabled={loading}
                >
                  {roles.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                      {role.is_system_role && ' (System)'}
                      <Chip 
                        label={`${role.permission_count} permissions`}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateRole}
                sx={{ 
                  minWidth: '140px',
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}
              >
                Create Role
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteRole}
                disabled={!selectedRoleId || selectedRole?.code === 'ADMIN'}
                sx={{ 
                  minWidth: '140px',
                  height: '56px',
                  whiteSpace: 'nowrap'
                }}
              >
                Delete Role
              </Button>
            </Stack>
          </Grid>
          
          {selectedRole && (
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Chip 
                  label={`${selectedRole.user_count || 0} users`}
                  color="primary"
                  size="small"
                />
                <Chip 
                  label={`${selectedRole.permission_count || 0} permissions`}
                  color="success"
                  size="small"
                />
                {selectedRole.code === 'ADMIN' && (
                  <Chip 
                    label="Protected Role"
                    color="warning"
                    size="small"
                    icon={<LockIcon />}
                  />
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Permission Matrix */}
      {loading ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Loading permissions...
          </Typography>
        </Paper>
      ) : (
        permissionMatrix && (
          <>
            <PermissionMatrix
              role={selectedRole}
              permissionData={permissionMatrix}
              permissionChanges={permissionChanges}
              onPermissionChange={handlePermissionChange}
              readOnly={selectedRole?.code === 'ADMIN'}
            />
            
            {/* Action Buttons */}
            {hasChanges && selectedRole?.code !== 'ADMIN' && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : `Save Changes (${Object.keys(permissionChanges).length})`}
                  </Button>
                </Stack>
              </Paper>
            )}
            
          </>
        )
      )}

      {/* Role Editor Dialog */}
      <RoleEditorDialog
        open={roleEditorOpen}
        role={editingRole}
        onClose={() => {
          setRoleEditorOpen(false);
          setEditingRole(null);
        }}
        onSave={handleSaveRole}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, data: null })}
        onConfirm={confirmAction}
        title={
          confirmDialog.action === 'cancel' 
            ? 'Discard Changes?' 
            : confirmDialog.action === 'deleteRole'
            ? 'Delete Role?'
            : 'Change Role?'
        }
        message={
          confirmDialog.action === 'cancel'
            ? 'You have unsaved changes. Are you sure you want to discard them?'
            : confirmDialog.action === 'deleteRole'
            ? `Are you sure you want to delete "${confirmDialog.data?.name}"? This action cannot be undone.`
            : 'You have unsaved changes. Changing roles will discard them. Continue?'
        }
        confirmText={
          confirmDialog.action === 'cancel' 
            ? 'Discard' 
            : confirmDialog.action === 'deleteRole'
            ? 'Delete'
            : 'Change Role'
        }
        severity="warning"
        confirmColor={confirmDialog.action === 'deleteRole' ? 'error' : 'warning'}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ open: false, title: '', message: '', severity: 'info' })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Box>
  );
};

export default RoleManagement;
